import gsap from 'gsap';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import useGsapContext from 'src/app/hooks/UseGsapContext';
import { selectScene, setActive } from 'src/app/redux/slice/sceneSlice';
import './Intro.scss';
import ScrollControls from '../../MobileControls/MobileControls';

const scenesImages = require.context('../../../../assets/scenes');
const logos = require.context('../../../../assets/logos');

const Intro = ({ data, allowScroll }) => {
  const dispatch = useDispatch();

  const isEnableNextSection = useRef(false);

  const containerRef = useRef();
  const introRef = useRef();
  const titleRef = useRef();
  const collageRef = useRef();
  const textboxRef = useRef();
  const logoDesktopRef = useRef();
  const logoMobileRef = useRef();

  const ctx = useGsapContext(containerRef);

  const lg = window.matchMedia('(min-width: 978px)');
  const xl = window.matchMedia('(min-width: 1200px)');
  const xxl = window.matchMedia('(min-width: 1400px)');
  const xxxl = window.matchMedia('(min-width: 1600px)');

  const changeScene = (progress) => {
    if (progress == 1 && !isEnableNextSection.current && lg.matches) {
      setTimeout(() => {
        window.scrollTo(0, 1950);
        isEnableNextSection.current = true;
        dispatch(setActive(true));
      }, 300);
    }

    if (progress == 1 && isEnableNextSection.current && lg.matches) {
      dispatch(setActive(false));
      gsap.to(introRef.current, {
        opacity: 0,
        duration: 0.8,
      });
      gsap.to(logoDesktopRef.current, {
        opacity: 0,
        duration: 0.8,
      });
      gsap.to(logoMobileRef.current, {
        opacity: 0,
        duration: 0.8,
      });
      setTimeout(() => {
        dispatch(selectScene('02'));
      }, 800);
    }
  };

  useEffect(() => {
    allowScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    ctx.add(() => {
      const parallax = gsap.utils.toArray('.parallax');

      gsap.set(introRef.current, { opacity: 0 });
      gsap.set(logoDesktopRef.current, { opacity: 0 });
      gsap.set(logoMobileRef.current, { opacity: 0 });
      gsap.to(introRef.current, {
        opacity: 1,
        keyframes: { y: [100, 0] },
      });
      gsap.to(logoMobileRef.current, {
        opacity: 1,
      });
      gsap.to(logoDesktopRef.current, {
        opacity: 1,
        keyframes: { y: [100, 0] },
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: introRef.current,
          scrub: true,
          pin: true,
          start: 'top top',
          invalidateOnRefresh: false,
          end: '+=2000',
          onUpdate: (self) => changeScene(self.progress),
        },
      });
      setTimeout(() => {
        // XXL -> 1600
        if (xxxl.matches) {
          parallax.forEach((item) => {
            tl.to(
              item,
              {
                top: 0,
                margin: 0,
                ease: 'none',
              },
              '<'
            );
          });
          tl.to(
            titleRef.current,
            {
              y:
                -titleRef.current.offsetHeight -
                textboxRef.current.offsetHeight +
                window.innerHeight -
                100,
              duration: 1,
            },
            '<'
          );
          tl.to(
            textboxRef.current,
            {
              y:
                -titleRef.current.offsetHeight -
                textboxRef.current.offsetHeight +
                window.innerHeight -
                100,
              duration: 1,
            },
            '<'
          );
        }
        // XXL -> 1400
        if (xxl.matches && !xxxl.matches) {
          parallax.forEach((item) => {
            tl.to(
              item,
              {
                top: 0,
                marginTop: 0,
                ease: 'none',
              },
              '<'
            );
          });
          tl.to(
            titleRef.current,
            {
              y:
                -textboxRef.current.offsetHeight -
                titleRef.current.offsetHeight +
                window.innerHeight -
                100,
              duration: 1,
            },
            '<'
          );
          tl.to(
            textboxRef.current,
            {
              y:
                -textboxRef.current.offsetHeight -
                titleRef.current.offsetHeight +
                window.innerHeight -
                100,
              duration: 1,
            },
            '<'
          );
        }
        // XL -> 1200
        if (xl.matches && !xxl.matches && !xxxl.matches) {
          parallax.forEach((item) => {
            tl.to(
              item,
              {
                top: 0,
                marginTop: 0,
                ease: 'none',
              },
              '<'
            );
          });
          tl.to(
            titleRef.current,
            {
              y:
                -textboxRef.current.offsetHeight -
                titleRef.current.offsetHeight +
                window.innerHeight -
                50,
              duration: 1,
            },
            '<'
          );
          tl.to(
            textboxRef.current,
            {
              y:
                -textboxRef.current.offsetHeight -
                titleRef.current.offsetHeight +
                window.innerHeight -
                50,
              duration: 1,
            },
            '<'
          );
        }
        // LG -> 925
        if (lg.matches && !xl.matches && !xxl.matches && !xxxl.matches) {
          parallax.forEach((item) => {
            tl.to(
              item,
              {
                top: 0,
                marginTop: 0,
                ease: 'none',
              },
              '<'
            );
          });
          tl.to(
            titleRef.current,
            {
              y:
                -textboxRef.current.offsetHeight -
                titleRef.current.offsetHeight +
                window.innerHeight -
                100,
              duration: 1,
            },
            '<'
          );
          tl.to(
            textboxRef.current,
            {
              y:
                -textboxRef.current.offsetHeight -
                titleRef.current.offsetHeight +
                window.innerHeight -
                100,
              duration: 1,
            },
            '<'
          );
        }
      }, 400);
    });
    return () => ctx.revert();
  });

  return (
    <div
      className="container"
      style={{ position: 'relative' }}
      ref={containerRef}
    >
      <div className="intro-logo-desktop" ref={logoDesktopRef}>
        <a href={data?.logo?.dir}>
          <img
            width="321px"
            height={'auto'}
            data-depth={'0.18'}
            src={logos(data?.logo?.url)}
            alt={data?.logo?.alt}
          />
        </a>
      </div>
      <div className="intro" ref={introRef}>
        <div className="intro-logo-mobile" ref={logoMobileRef}>
          <a href={data?.logo?.dir}>
            <img
              width="321px"
              height={'auto'}
              data-depth={'0.18'}
              src={logos(data?.logo?.url)}
              alt={data?.logo?.alt}
            />
          </a>
        </div>
        <div className="intro-title" ref={titleRef}>
          <h2>{data?.title}</h2>
        </div>
        <div className="intro-collage" ref={collageRef}>
          <div className="intro-collage-parallax">
            <img
              aria-hidden="true"
              className="img1 parallax"
              data-depth={'0.20'}
              src={scenesImages(data?.collage?.img1?.url)}
              alt=""
              width="321px"
            />
            <img
              className="img2 parallax"
              width="321px"
              height={'auto'}
              data-depth={'0.18'}
              src={scenesImages(data?.collage?.img2?.url)}
              alt="Collage: precursores de CIAT realizando una planacion para el desarrollo de la empresa"
            />
            <img
              aria-hidden="true"
              className="img3 parallax"
              data-depth={'0.17'}
              src={scenesImages(data?.collage?.img3?.url)}
              width="321px"
              alt=""
            />
          </div>
        </div>
        <div className="intro-textbox" ref={textboxRef}>
          <h3>{data?.subtitle}</h3>
          <div
            className="intro-textbox-format"
            dangerouslySetInnerHTML={{ __html: data?.description }}
          ></div>
          <ScrollControls next={'02'} />
        </div>
      </div>
    </div>
  );
};

export default Intro;
